/**
 * Course grading policy.
 *
 * Replaces Studio's "Grading" tab. The part that matters is the arithmetic, not the form:
 *
 * WEIGHTS MUST TOTAL 100. The platform does not check. `CourseGradingModel` stores each
 * weight divided by 100 and the grader then computes every learner's grade against a
 * scale that does not add up — a course that looks configured and quietly mis-grades for
 * a whole run, discovered when someone disputes a certificate. So the total is shown
 * live, the shortfall is stated in words, and the save is held until it resolves.
 *
 * DROP COUNT MUST BE BELOW MIN COUNT. "Drop the lowest 2 of 1" drops everything the
 * learner ever submits in that category, which reads as a bug in grading rather than a
 * configuration mistake, so it is caught per row.
 *
 * Assignment types are also LOAD-BEARING NAMES: subsections reference their grader by
 * type string. Renaming or removing one silently unhooks every subsection using it, so
 * the current usage from `course_assignment_lists` is shown against each row.
 */

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { Badge, Banner, Button, Card, Field, Spinner } from '../../shared/ui';
import { PageHeader } from '../AdminShell';
import { fractionPercent, getCourseVerdict } from '../../shared/lib/answers';
import {
  WEIGHT_TOTAL,
  explainFailure,
  getCourseGrading,
  resolveCourseKey,
  saveCourseGrading,
  totalWeight,
  type CourseGradingContext,
  type GradingModel,
} from '../../shared/lib/studio-settings';
import './course-settings.css';

/** Rows are edited as text so a half-typed number is not silently coerced to 0. */
interface GraderRow {
  key: string;
  type: string;
  short_label: string;
  weight: string;
  min_count: string;
  drop_count: string;
}

interface CutoffRow {
  key: string;
  letter: string;
  /** Percentage for editing; the API stores a fraction of 1. */
  percent: string;
}

interface Draft {
  graders: GraderRow[];
  cutoffs: CutoffRow[];
  hasGrace: boolean;
  graceHours: string;
  graceMinutes: string;
  minimumGradeCredit: string;
}

let rowSeq = 0;
const nextKey = (prefix: string) => `${prefix}-${(rowSeq += 1)}`;

/** Fraction of 1 → a percentage with one decimal, without float noise (0.749999 → 75). */
const asPercent = (fraction: number) => String(Math.round(fraction * 1000) / 10);

function toDraft(model: GradingModel): Draft {
  return {
    graders: model.graders.map((grader) => ({
      key: nextKey('g'),
      type: grader.type,
      short_label: grader.short_label ?? '',
      weight: String(grader.weight),
      min_count: String(grader.min_count),
      drop_count: String(grader.drop_count),
    })),
    // Highest first: that is the order a grade scale reads in, and the order the
    // descending check below reports against.
    cutoffs: Object.entries(model.grade_cutoffs)
      .sort(([, a], [, b]) => b - a)
      .map(([letter, fraction]) => ({ key: nextKey('c'), letter, percent: asPercent(fraction) })),
    hasGrace: model.grace_period !== null,
    graceHours: String(model.grace_period?.hours ?? 0),
    graceMinutes: String(model.grace_period?.minutes ?? 0),
    minimumGradeCredit: asPercent(model.minimum_grade_credit ?? 0),
  };
}

const num = (value: string) => (value.trim() === '' ? Number.NaN : Number(value));

export default function CourseGrading() {
  const params = useParams();
  const [search] = useSearchParams();
  const courseKey = resolveCourseKey(params, search);
  const queryClient = useQueryClient();

  const grading = useQuery({
    queryKey: ['course-grading', courseKey],
    queryFn: () => getCourseGrading(courseKey as string),
    enabled: Boolean(courseKey),
  });

  /**
   * Can this policy pass anyone, as saved — not as drafted. AC-045 / spec §2.5: the platform
   * accepts a `min_count`/`drop_count`/weight combination whose maximum achievable score sits
   * below the pass cutoff without ever refusing the save, so the only way to catch it is to ask
   * the server to do the arithmetic against the policy that is actually live. Its own query
   * (not derived from `grading` above) because it survives a draft in progress: an author
   * mid-edit still needs to see whether the CURRENTLY SAVED policy can pass anyone.
   */
  const verdict = useQuery({
    queryKey: ['course-verdict', courseKey],
    queryFn: () => getCourseVerdict(courseKey as string),
    enabled: Boolean(courseKey),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [baseline, setBaseline] = useState('');

  // Seed once, so a background refetch cannot discard edits in progress.
  useEffect(() => {
    if (grading.data && draft === null) {
      const seeded = toDraft(grading.data.course_details);
      setDraft(seeded);
      setBaseline(JSON.stringify(seeded));
    }
  }, [grading.data, draft]);

  const save = useMutation({
    mutationFn: (model: GradingModel) => saveCourseGrading(courseKey as string, model),
    onSuccess: (saved) => {
      queryClient.setQueryData<CourseGradingContext>(['course-grading', courseKey], (current) =>
        current ? { ...current, course_details: saved } : current,
      );
      const reseeded = toDraft(saved);
      setDraft(reseeded);
      setBaseline(JSON.stringify(reseeded));
    },
  });

  if (!courseKey) {
    return (
      <>
        <PageHeader title="Grading" />
        <Banner tone="warning">
          <strong>No course selected.</strong> Open this page from a course, or add the
          course ID to the address as <code>?course=course-v1:ORG+NUMBER+RUN</code>.
        </Banner>
      </>
    );
  }

  if (grading.isLoading) {
    return (
      <>
        <PageHeader title="Grading" />
        <Spinner label="Loading the grading policy…" />
      </>
    );
  }

  if (grading.isError) {
    return (
      <>
        <PageHeader title="Grading" />
        <Banner tone="danger">
          <strong>Could not load the grading policy.</strong> {explainFailure(grading.error)}{' '}
          Nothing has been changed.{' '}
          <button type="button" className="ui-btn" data-variant="ghost" onClick={() => grading.refetch()}>
            Try again
          </button>
        </Banner>
      </>
    );
  }

  const context = grading.data;
  if (!context || !draft) {
    return (
      <>
        <PageHeader title="Grading" />
        <Spinner label="Preparing the editor…" />
      </>
    );
  }

  const usage = context.course_assignment_lists ?? {};
  const dirty = JSON.stringify(draft) !== baseline;

  const setGrader = (key: string, patch: Partial<GraderRow>) =>
    setDraft({
      ...draft,
      graders: draft.graders.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    });

  const setCutoff = (key: string, patch: Partial<CutoffRow>) =>
    setDraft({
      ...draft,
      cutoffs: draft.cutoffs.map((row) => (row.key === key ? { ...row, ...patch } : row)),
    });

  /* ---- validation ---------------------------------------------------------------- */

  const graderIssues = new Map<string, string>();
  const seenTypes = new Set<string>();

  draft.graders.forEach((row) => {
    const weight = num(row.weight);
    const minCount = num(row.min_count);
    const dropCount = num(row.drop_count);
    const type = row.type.trim();

    if (type === '') {
      graderIssues.set(row.key, 'Give this assignment type a name — subsections choose their grader by it.');
    } else if (seenTypes.has(type.toLowerCase())) {
      graderIssues.set(row.key, `“${type}” is used twice. Two types with one name cannot be told apart.`);
    } else if (Number.isNaN(weight) || weight < 0 || weight > 100) {
      graderIssues.set(row.key, 'Weight must be a number between 0 and 100.');
    } else if (Number.isNaN(minCount) || minCount < 1) {
      graderIssues.set(row.key, 'There must be at least one assignment of this type.');
    } else if (Number.isNaN(dropCount) || dropCount < 0) {
      graderIssues.set(row.key, 'Dropped assignments cannot be negative.');
    } else if (dropCount >= minCount) {
      graderIssues.set(
        row.key,
        `Dropping ${dropCount} of ${minCount} leaves nothing to grade. Drop fewer than the total.`,
      );
    }
    if (type !== '') seenTypes.add(type.toLowerCase());
  });

  const weights = draft.graders.map((row) => (Number.isNaN(num(row.weight)) ? 0 : num(row.weight)));
  const total = totalWeight(weights);
  const weightsOk = total === WEIGHT_TOTAL;

  const cutoffIssues = new Map<string, string>();
  const seenLetters = new Set<string>();
  let previous = Number.POSITIVE_INFINITY;

  draft.cutoffs.forEach((row) => {
    const percent = num(row.percent);
    const letter = row.letter.trim();

    if (letter === '') {
      cutoffIssues.set(row.key, 'Name this grade.');
    } else if (seenLetters.has(letter.toLowerCase())) {
      cutoffIssues.set(row.key, `“${letter}” appears twice.`);
    } else if (Number.isNaN(percent) || percent <= 0 || percent >= 100) {
      cutoffIssues.set(row.key, 'Use a percentage above 0 and below 100.');
    } else if (percent >= previous) {
      // Cutoffs are lower bounds, so equal or rising values make the grade below
      // unreachable — a learner can never land in it.
      cutoffIssues.set(row.key, 'Each grade must have a lower cutoff than the one above it.');
    }
    if (letter !== '') seenLetters.add(letter.toLowerCase());
    if (!Number.isNaN(percent)) previous = percent;
  });

  const graceMinutes = num(draft.graceMinutes);
  const graceHours = num(draft.graceHours);
  const graceOk =
    !draft.hasGrace ||
    (!Number.isNaN(graceHours) && graceHours >= 0 && !Number.isNaN(graceMinutes) && graceMinutes >= 0 && graceMinutes < 60);

  const creditPercent = num(draft.minimumGradeCredit);
  const creditOk =
    !context.is_credit_course || (!Number.isNaN(creditPercent) && creditPercent >= 0 && creditPercent <= 100);

  const passingCutoff = draft.cutoffs.reduce((lowest, row) => {
    const percent = num(row.percent);
    return Number.isNaN(percent) ? lowest : Math.min(lowest, percent);
  }, Number.POSITIVE_INFINITY);

  const blocked =
    graderIssues.size > 0 ||
    cutoffIssues.size > 0 ||
    draft.cutoffs.length === 0 ||
    !weightsOk ||
    !graceOk ||
    !creditOk;

  const submit = () => {
    const model: GradingModel = {
      // `id` is the position; the server re-indexes on read, and `parse_grader` ignores
      // whatever we send. Order is what identifies a grader.
      graders: draft.graders.map((row, index) => ({
        id: index,
        type: row.type.trim(),
        short_label: row.short_label.trim(),
        weight: num(row.weight),
        min_count: num(row.min_count),
        drop_count: num(row.drop_count),
      })),
      grade_cutoffs: Object.fromEntries(
        draft.cutoffs.map((row) => [row.letter.trim(), Math.round(num(row.percent) * 10) / 1000]),
      ),
      // Null means "never had a grace period". Once one exists the platform cannot clear
      // it, so removing it here means zero hours zero minutes, not null.
      grace_period: draft.hasGrace
        ? { hours: num(draft.graceHours), minutes: num(draft.graceMinutes) }
        : null,
      // NaN would serialise as null and the platform would store None, so an empty field
      // means "no minimum" rather than a broken write.
      minimum_grade_credit: Number.isNaN(creditPercent) ? 0 : Math.round(creditPercent * 10) / 1000,
    };
    save.mutate(model);
  };

  const suggestLetter = () => {
    const used = new Set(draft.cutoffs.map((row) => row.letter.trim().toUpperCase()));
    return context.default_grade_designations.find((letter) => !used.has(letter.toUpperCase())) ?? '';
  };

  return (
    <>
      <PageHeader
        title="Grading"
        description={`Assignment weights, grade scale and grace period for ${courseKey}.`}
      />

      {save.isError && (
        <Banner tone="danger">
          <strong>The grading policy was not saved.</strong> {explainFailure(save.error)} The
          course is still grading on its previous policy.
        </Banner>
      )}
      {save.isSuccess && !dirty && (
        <Banner tone="success">
          <strong>Grading policy saved.</strong> Grades are recalculated from the new policy
          as learners submit work; existing scores are re-weighted on their next visit.
        </Banner>
      )}

      <form
        className="cs-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!blocked) submit();
        }}
      >
        <Card
          title="Assignment types"
          action={
            <Button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  graders: [
                    ...draft.graders,
                    { key: nextKey('g'), type: '', short_label: '', weight: '0', min_count: '1', drop_count: '0' },
                  ],
                })
              }
            >
              Add type
            </Button>
          }
        >
          <div className="cs-editor">
            <table>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Abbreviation</th>
                  <th className="num">Weight %</th>
                  <th className="num">Total count</th>
                  <th className="num">Drop lowest</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.graders.map((row) => {
                  const inUse = usage[row.type] ?? [];
                  const issue = graderIssues.get(row.key);
                  return (
                    <tr key={row.key}>
                      <td>
                        <input
                          className="cs-input"
                          aria-label="Assignment type"
                          value={row.type}
                          data-invalid={issue ? 'true' : undefined}
                          onChange={(event) => setGrader(row.key, { type: event.target.value })}
                        />
                        {issue && <p className="cs-issue">{issue}</p>}
                        {inUse.length > 0 && (
                          <p className="cs-usage">
                            Used by {inUse.length} subsection{inUse.length === 1 ? '' : 's'}.
                            Renaming it unhooks {inUse.length === 1 ? 'that one' : 'them'}.
                          </p>
                        )}
                      </td>
                      <td>
                        <input
                          className="cs-input cs-input--narrow"
                          aria-label="Abbreviation"
                          value={row.short_label}
                          onChange={(event) => setGrader(row.key, { short_label: event.target.value })}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="cs-input cs-input--narrow"
                          aria-label="Weight percent"
                          type="number"
                          min={0}
                          max={100}
                          value={row.weight}
                          onChange={(event) => setGrader(row.key, { weight: event.target.value })}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="cs-input cs-input--narrow"
                          aria-label="Total number of assignments"
                          type="number"
                          min={1}
                          value={row.min_count}
                          onChange={(event) => setGrader(row.key, { min_count: event.target.value })}
                        />
                      </td>
                      <td className="num">
                        <input
                          className="cs-input cs-input--narrow"
                          aria-label="Number of lowest scores to drop"
                          type="number"
                          min={0}
                          value={row.drop_count}
                          onChange={(event) => setGrader(row.key, { drop_count: event.target.value })}
                        />
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          type="button"
                          onClick={() =>
                            setDraft({
                              ...draft,
                              graders: draft.graders.filter((other) => other.key !== row.key),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {draft.graders.length === 0 && (
            <p className="cs-note">
              No assignment types. Every graded subsection needs one, so add at least one
              type weighted at 100%.
            </p>
          )}

          {/* The running total, always visible — see the file header for why. */}
          <div className="cs-total" data-state={weightsOk ? 'ok' : 'off'}>
            <b>{total}%</b>
            <span>
              {weightsOk
                ? 'Weights total 100%.'
                : total < WEIGHT_TOTAL
                  ? `${Math.round((WEIGHT_TOTAL - total) * 100) / 100}% of the final grade is unassigned. Saving now would under-count every learner.`
                  : `${Math.round((total - WEIGHT_TOTAL) * 100) / 100}% over. Saving now would over-count every learner.`}
            </span>
          </div>

          {Object.keys(usage).length > 0 && (
            <p className="cs-note">
              Types currently in use: {Object.keys(usage).join(', ')}. Removing one leaves its
              subsections graded by nothing.
            </p>
          )}

          {/*
            AC-045. Weights summing to 100% (checked above) says nothing about whether the
            counts and drops below can ever REACH the pass mark — a course can pass every
            weight check here and still be mathematically unable to pass a single learner.
            Placed beside the fields that cause it, not just at the top of the page, because
            the fix is a `min_count` or `drop_count` a row above.
          */}
          {verdict.data?.grading === 'misconfigured' && (
            <Banner tone="danger">
              <strong>
                This course cannot pass anyone with its current grading policy.
              </strong>{' '}
              The pass mark is {fractionPercent(verdict.data.pass_cutoff) ?? '?'}%, but the
              highest score any learner can reach is{' '}
              {fractionPercent(verdict.data.max_achievable_percent) ?? '0'}%.
              {(verdict.data.arithmetic ?? []).length > 0 && (
                <ul className="cs-arithmetic">
                  {(verdict.data.arithmetic ?? []).map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              )}
            </Banner>
          )}

          {verdict.data?.warning_empty_graded_subsections && (
            <Banner tone="warning">{verdict.data.warning_empty_graded_subsections}</Banner>
          )}
        </Card>

        <Card
          title="Grade scale"
          action={
            <Button
              type="button"
              onClick={() =>
                setDraft({
                  ...draft,
                  cutoffs: [
                    ...draft.cutoffs,
                    { key: nextKey('c'), letter: suggestLetter(), percent: '50' },
                  ],
                })
              }
            >
              Add grade
            </Button>
          }
        >
          <p className="cs-note">
            Each row is the lowest score that earns that grade, highest first. The bottom row
            is the pass mark: below it a learner does not pass the course.
          </p>

          <div className="cs-editor">
            <table>
              <thead>
                <tr>
                  <th>Grade</th>
                  <th className="num">From %</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {draft.cutoffs.map((row) => {
                  const issue = cutoffIssues.get(row.key);
                  return (
                    <tr key={row.key}>
                      <td>
                        <input
                          className="cs-input cs-input--narrow"
                          aria-label="Grade name"
                          value={row.letter}
                          data-invalid={issue ? 'true' : undefined}
                          onChange={(event) => setCutoff(row.key, { letter: event.target.value })}
                        />
                        {issue && <p className="cs-issue">{issue}</p>}
                      </td>
                      <td className="num">
                        <input
                          className="cs-input cs-input--narrow"
                          aria-label="Lowest percentage for this grade"
                          type="number"
                          min={1}
                          max={99}
                          value={row.percent}
                          onChange={(event) => setCutoff(row.key, { percent: event.target.value })}
                        />
                      </td>
                      <td>
                        <Button
                          variant="danger"
                          type="button"
                          disabled={draft.cutoffs.length === 1}
                          onClick={() =>
                            setDraft({
                              ...draft,
                              cutoffs: draft.cutoffs.filter((other) => other.key !== row.key),
                            })
                          }
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {draft.cutoffs.length === 0 && (
            <Banner tone="danger">
              <strong>No grades defined.</strong> Without a scale nobody can pass. Add at
              least one grade — a single row named &ldquo;Pass&rdquo; makes the course
              pass/fail.
            </Banner>
          )}
          {draft.cutoffs.length === 1 && Number.isFinite(passingCutoff) && (
            <p className="cs-note">
              One grade: this course is pass/fail at {passingCutoff}%.
            </p>
          )}
        </Card>

        <Card title="Deadlines">
          <label className="cs-check">
            <input
              type="checkbox"
              checked={draft.hasGrace}
              onChange={(event) => setDraft({ ...draft, hasGrace: event.target.checked })}
            />
            <span>
              Allow a grace period after each due date
              <small>
                Work submitted inside the grace period still counts as on time. Once a grace
                period has been saved the platform cannot remove it again — set it to zero
                hours and zero minutes instead.
              </small>
            </span>
          </label>

          {draft.hasGrace && (
            <div className="cs-grid">
              <Field
                label="Hours"
                name="grace_hours"
                type="number"
                min={0}
                value={draft.graceHours}
                onChange={(event) => setDraft({ ...draft, graceHours: event.target.value })}
                error={
                  Number.isNaN(graceHours) || graceHours < 0
                    ? 'Enter a whole number of hours, or 0.'
                    : undefined
                }
              />
              <Field
                label="Minutes"
                name="grace_minutes"
                type="number"
                min={0}
                max={59}
                value={draft.graceMinutes}
                onChange={(event) => setDraft({ ...draft, graceMinutes: event.target.value })}
                error={
                  Number.isNaN(graceMinutes) || graceMinutes < 0 || graceMinutes > 59
                    ? 'Minutes must be between 0 and 59 — use the hours field for longer.'
                    : undefined
                }
              />
            </div>
          )}
        </Card>

        {/* Credit fields only exist for a credit-bearing course; elsewhere the value is
            stored but nothing reads it. */}
        {context.is_credit_course && (
          <Card title="Credit">
            <div className="cs-grid">
              <Field
                label="Minimum grade for credit (%)"
                name="minimum_grade_credit"
                type="number"
                min={0}
                max={100}
                value={draft.minimumGradeCredit}
                onChange={(event) => setDraft({ ...draft, minimumGradeCredit: event.target.value })}
                error={creditOk ? undefined : 'Enter a percentage between 0 and 100.'}
                hint="Learners need at least this overall grade to earn academic credit."
              />
            </div>
            {creditOk && Number.isFinite(passingCutoff) && creditPercent < passingCutoff && (
              <Banner tone="warning">
                The minimum grade for credit ({creditPercent}%) is below the pass mark (
                {passingCutoff}%), so a learner could qualify for credit without passing.
              </Banner>
            )}
            {!context.show_credit_eligibility && (
              <p className="cs-note">
                Credit eligibility is not switched on for this deployment, so this value is
                stored but not yet acted on.
              </p>
            )}
          </Card>
        )}

        <div className="cs-bar">
          <span className="cs-bar__state" data-dirty={dirty ? 'true' : 'false'}>
            {dirty ? 'Unsaved changes' : 'All changes saved'}
          </span>
          {!weightsOk && <Badge tone="danger">Weights total {total}%, not 100%</Badge>}
          {weightsOk && (graderIssues.size > 0 || cutoffIssues.size > 0) && (
            <Badge tone="danger">{graderIssues.size + cutoffIssues.size} rows need attention</Badge>
          )}
          <div className="cs-bar__end">
            {dirty && (
              <Button
                variant="ghost"
                type="button"
                onClick={() => {
                  if (grading.data) {
                    const reseeded = toDraft(grading.data.course_details);
                    setDraft(reseeded);
                    setBaseline(JSON.stringify(reseeded));
                  }
                }}
              >
                Discard changes
              </Button>
            )}
            <Button type="submit" variant="primary" busy={save.isPending} disabled={!dirty || blocked}>
              Save grading policy
            </Button>
          </div>
        </div>
      </form>
    </>
  );
}
